import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Activity, Check, Circle } from "lucide-react";

import { useWizardFlow } from "@/hooks/useWizardFlow";
import { cn } from "@/lib/cn";
import { strings } from "@/lib/strings";
import { WIZARD_STEPS, wizardLocationPath, wizardRoutePath } from "@/lib/steps";

export function Sidebar() {
  const location = useLocation();
  const flow = useWizardFlow();

  const isLive = location.pathname === "/" || location.pathname === "/live";

  const activeIndex = WIZARD_STEPS.findIndex((step) => {
    const absolute = wizardLocationPath(step.segment);
    return location.pathname === absolute || location.pathname.startsWith(`${absolute}/`);
  });

  return (
    <aside className="relative hidden w-72 shrink-0 flex-col border-r border-border/60 bg-card/50 p-5 backdrop-blur-xl lg:flex">
      <div className="mb-6 flex items-center gap-3 border-b border-border/60 pb-5">
        <div className="grid size-10 place-items-center rounded-lg border border-border/60 bg-card">
          <svg viewBox="0 0 24 24" fill="none" className="size-5 text-primary">
            <path
              d="M4 16c0-5 2.5-9 8-9s8 4 8 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="4" cy="16" r="1.5" fill="currentColor" />
            <circle cx="20" cy="16" r="1.5" fill="currentColor" />
            <path d="M7 19h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {strings.app.name}
          </div>
          <div className="text-sm font-semibold text-foreground">{strings.app.subtitle}</div>
        </div>
      </div>

      <nav aria-label="Console navigation" className="relative flex-1">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
          {strings.nav.observability}
        </p>
        <div className="mb-4">
          <Link
            to="/"
            className={cn(
              "group relative flex items-start gap-3 rounded-lg px-2.5 py-2.5 transition-colors",
              isLive
                ? "border border-primary/30 bg-primary/10 text-foreground"
                : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "relative z-10 mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border bg-background text-[10px]",
                isLive && "border-primary bg-primary text-primary-foreground",
                !isLive && "border-border",
              )}
            >
              <Activity className="size-3" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium leading-tight">{strings.nav.live}</span>
              <span className="text-[11px] leading-snug text-muted-foreground">{strings.live.title}</span>
            </span>
            {isLive ? (
              <motion.span
                layoutId="sidebar-active-indicator"
                className="absolute inset-y-1 right-1 w-0.5 rounded-full bg-primary"
                transition={{ type: "spring", stiffness: 250, damping: 28 }}
              />
            ) : null}
          </Link>
        </div>

        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
          {strings.nav.bootstrap}
        </p>
        <div className="relative">
          <div className="absolute bottom-0 left-[15px] top-2 w-px bg-border/60" aria-hidden />
          <ol className="space-y-0.5">
          {WIZARD_STEPS.map((step, index) => {
            const stepFlow = flow.steps[step.key];
            if (!stepFlow.visible) {
              return null;
            }

            const isActive = index === activeIndex;
            const isDone = stepFlow.completed && !isActive;
            const canNavigate = stepFlow.accessible || isDone;

            const rowClass = cn(
              "group relative flex items-start gap-3 rounded-lg px-2.5 py-2.5 transition-colors",
              isActive
                ? "border border-primary/30 bg-primary/10 text-foreground"
                : canNavigate
                  ? "text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                  : "cursor-not-allowed text-muted-foreground/60",
            );

            const indicator = (
              <span
                className={cn(
                  "relative z-10 mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border bg-background text-[10px]",
                  isActive && "border-primary bg-primary text-primary-foreground",
                  isDone && "border-success bg-success text-success-foreground",
                  !isActive && !isDone && "border-border",
                )}
              >
                {isDone ? <Check className="size-3" /> : <Circle className="size-1.5 fill-current" />}
              </span>
            );

            const label = (
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium leading-tight">{step.title}</span>
                <span className="text-[11px] leading-snug text-muted-foreground">{step.description}</span>
              </span>
            );

            return (
              <li key={step.key}>
                {canNavigate ? (
                  <Link to={wizardRoutePath(step.segment)} className={rowClass}>
                    {indicator}
                    {label}
                    {isActive ? (
                      <motion.span
                        layoutId="sidebar-active-indicator"
                        className="absolute inset-y-1 right-1 w-0.5 rounded-full bg-primary"
                        transition={{ type: "spring", stiffness: 250, damping: 28 }}
                      />
                    ) : null}
                  </Link>
                ) : (
                  <span className={rowClass} aria-disabled>
                    {indicator}
                    {label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        </div>
      </nav>

    </aside>
  );
}
